const normalizeKey = (key) => String(key).replace(/[^a-z0-9]/gi, '').toLowerCase();

export const dedupeResponseKeys = (value) => {
  if (Array.isArray(value)) {
    return value.map(dedupeResponseKeys);
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const seenKeys = new Set();
  return Object.entries(value).reduce((result, [key, entryValue]) => {
    const normalizedKey = normalizeKey(key);
    if (seenKeys.has(normalizedKey)) return result;
    seenKeys.add(normalizedKey);
    result[key] = dedupeResponseKeys(entryValue);
    return result;
  }, {});
};

export const buildEndpointOptions = (apiUrls = {}) => {
  const sortedEntries = Object.entries(apiUrls)
    .filter(([, url]) => typeof url === 'string' && url.trim())
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey, undefined, {
      sensitivity: 'base',
    }));
  const optionsByUrl = new Map();

  sortedEntries.forEach(([key, url]) => {
    const normalizedUrl = url.trim();
    const existing = optionsByUrl.get(normalizedUrl);
    if (existing) {
      existing.aliases.push(key);
      return;
    }
    optionsByUrl.set(normalizedUrl, { key, url: normalizedUrl, aliases: [] });
  });

  return Array.from(optionsByUrl.values());
};

export const filterEndpointOptions = (options, query) => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return options;
  return options.filter((option) => [option.key, option.url, ...option.aliases]
    .some((value) => String(value).toLowerCase().includes(normalizedQuery)));
};

export const filterResponseData = (value, query) => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery || value === null || value === undefined) return value;

  const matches = (entry) => {
    if (Array.isArray(entry)) return entry.some(matches);
    if (entry && typeof entry === 'object') {
      return Object.entries(entry).some(([key, nestedValue]) => key.toLowerCase().includes(normalizedQuery) || matches(nestedValue));
    }
    return String(entry).toLowerCase().includes(normalizedQuery);
  };

  if (Array.isArray(value)) {
    return value.filter(matches);
  }

  if (typeof value === 'object') {
    return matches(value) ? value : {};
  }

  return matches(value) ? value : null;
};

export const getResponseCount = (value) => {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === 'object') return Object.keys(value).length > 0 ? 1 : 0;
  return value === null || value === undefined || value === '' ? 0 : 1;
};

export const parseDeleteIds = (value, maximumRangeSize = 100) => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) throw new Error('Enter an ID or an ID range.');

  if (!trimmedValue.includes('-')) {
    const id = Number(trimmedValue);
    if (!Number.isInteger(id) || id < 0) throw new Error('Enter a valid numeric ID.');
    return [id];
  }

  const match = trimmedValue.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!match) throw new Error('Use a range such as 10-15.');
  const low = Number(match[1]);
  const high = Number(match[2]);
  if (low > high) throw new Error('The first range ID must be lower than the second.');
  if ((high - low) + 1 > maximumRangeSize) {
    throw new Error(`Delete ranges are limited to ${maximumRangeSize} records at a time.`);
  }
  return Array.from({ length: (high - low) + 1 }, (_, index) => low + index);
};

export const parseDeleteRequestTargets = (identifierValue, requestObjectText, maximumCount = 100) => {
  const trimmedObject = String(requestObjectText || '').trim();
  if (!trimmedObject) {
    return parseDeleteIds(identifierValue, maximumCount).map((id) => ({
      label: String(id),
      lookupValue: id,
      payload: { ID: id },
    }));
  }

  let parsed;
  try {
    parsed = JSON.parse(trimmedObject);
  } catch (_) {
    throw new Error('The DELETE request object is not valid JSON.');
  }

  const payloads = Array.isArray(parsed) ? parsed : [parsed];
  if (payloads.length === 0) throw new Error('Enter at least one DELETE request object.');
  if (payloads.length > maximumCount) {
    throw new Error(`DELETE requests are limited to ${maximumCount} records at a time.`);
  }
  if (payloads.some((payload) => !payload || typeof payload !== 'object' || Array.isArray(payload) || Object.keys(payload).length === 0)) {
    throw new Error('Each DELETE request must be a non-empty JSON object.');
  }

  return payloads.map((payload, index) => {
    const identifierEntry = Object.entries(payload).find(([key]) => normalizeKey(key) === 'id')
      || Object.entries(payload).find(([key]) => normalizeKey(key).endsWith('id'));
    return {
      label: identifierEntry ? String(identifierEntry[1]) : `object ${index + 1}`,
      lookupValue: identifierEntry?.[1],
      payload,
    };
  });
};

export const unwrapRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && typeof payload.data === 'object') return Object.values(payload.data);
  return [];
};

const collectErrorText = (value, parts = []) => {
  if (typeof value === 'string') {
    parts.push(value);
    return parts;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectErrorText(item, parts));
    return parts;
  }
  if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectErrorText(item, parts));
  }
  return parts;
};

export const getRequiredDeleteFields = (error = {}) => {
  const fields = [];
  const addField = (field) => {
    const cleanField = String(field || '').replace(/^[^a-z]+|[^a-z0-9_]+$/gi, '');
    if (cleanField && !fields.some((current) => normalizeKey(current) === normalizeKey(cleanField))) {
      fields.push(cleanField);
    }
  };
  const validationErrors = error?.details?.errors || error?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    Object.entries(validationErrors).forEach(([field, messages]) => {
      if (collectErrorText(messages).some((message) => /required|missing/i.test(message))) addField(field);
    });
  }

  collectErrorText([error?.message, error?.details, error?.data]).forEach((message) => {
    const patterns = [
      /\b([A-Za-z][A-Za-z0-9_]*)\s+(?:parameter|field|property)\s+is\s+(?:missing|required)\b/gi,
      /\b(?:missing|required)\s+(?:parameter|field|property)?\s*[:=-]?\s*['"]?([A-Za-z][A-Za-z0-9_]*)/gi,
    ];
    patterns.forEach((pattern) => {
      let match = pattern.exec(message);
      while (match) {
        addField(match[1]);
        match = pattern.exec(message);
      }
    });
  });
  return fields;
};

const getRecordEntry = (record, field) => Object.entries(record || {})
  .find(([key]) => normalizeKey(key) === normalizeKey(field));

const valuesMatch = (left, right) => String(left ?? '') === String(right ?? '');

const resolveDeleteFieldValue = (records, payload, field, lookupValue) => {
  const comparablePayloadEntries = Object.entries(payload).filter(([key, value]) => (
    normalizeKey(key) !== 'id' && value !== null && value !== undefined && value !== ''
  ));
  let candidates = records.filter((record) => comparablePayloadEntries.every(([key, value]) => {
    const recordEntry = getRecordEntry(record, key);
    return !recordEntry || valuesMatch(recordEntry[1], value);
  }));

  if (candidates.length === 0) candidates = records;
  const candidateValues = candidates
    .map((record) => getRecordEntry(record, field)?.[1])
    .filter((value) => value !== undefined && value !== null && value !== '');
  const uniqueValues = [...new Map(candidateValues.map((value) => [String(value), value])).values()];
  if (uniqueValues.length === 1) return uniqueValues[0];

  const lookupMatches = uniqueValues.filter((value) => valuesMatch(value, lookupValue));
  if (lookupMatches.length === 1) return lookupMatches[0];

  const payloadKeys = Object.keys(payload);
  if (
    lookupValue !== undefined &&
    payloadKeys.length === 1 &&
    normalizeKey(payloadKeys[0]) === 'id' &&
    normalizeKey(field) !== 'id'
  ) {
    return lookupValue;
  }
  return undefined;
};

export const augmentDeletePayloadFromError = (error, payload, responsePayload, lookupValue) => {
  const requiredFields = getRequiredDeleteFields(error);
  if (requiredFields.length === 0) return null;

  const records = unwrapRecords(responsePayload);
  const nextPayload = { ...payload };
  const resolvedFields = [];
  const unresolvedFields = [];

  requiredFields.forEach((field) => {
    const existingEntry = getRecordEntry(nextPayload, field);
    const existingValue = existingEntry?.[1];
    if (existingValue !== undefined && existingValue !== null && existingValue !== '') return;

    const value = resolveDeleteFieldValue(records, nextPayload, field, lookupValue);
    if (value === undefined) {
      nextPayload[existingEntry?.[0] || field] = null;
      unresolvedFields.push(field);
    } else {
      nextPayload[existingEntry?.[0] || field] = value;
      resolvedFields.push(field);
    }
  });

  const originalKeys = Object.keys(payload);
  if (
    originalKeys.length === 1 &&
    normalizeKey(originalKeys[0]) === 'id' &&
    resolvedFields.some((field) => normalizeKey(field) !== 'id')
  ) {
    delete nextPayload[originalKeys[0]];
  }

  return { payload: nextPayload, requiredFields, resolvedFields, unresolvedFields };
};

export const findProjectRecords = (payload, projectId, tableName) => {
  const targetId = String(projectId);
  return unwrapRecords(payload)
    .filter((item) => String(
      item?.ProjectID ?? item?.projectID ?? item?.ProjectId ?? item?.projectId ?? ''
    ) === targetId)
    .map((item) => ({
      tableName,
      id: item?.ID ?? item?.id ?? '',
      zone: item?.Zone ?? item?.zone ?? '',
      content: dedupeResponseKeys(item),
    }));
};

export const dedupeSearchResults = (results) => {
  const seen = new Set();
  return results.filter((result) => {
    const key = [result.tableName, result.id, result.zone, JSON.stringify(result.content)].join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const normalizeRequestError = (error) => (
  error?.data?.message ||
  error?.data?.error ||
  (typeof error?.data === 'string' ? error.data : '') ||
  error?.details?.message ||
  error?.details?.error ||
  (typeof error?.details === 'string' ? error.details : '') ||
  error?.error ||
  error?.message ||
  'The request failed.'
);
